import React, { useEffect, useState } from "react";
import { Paperclip, Send, Upload, X } from "lucide-react";

const DEFAULT_MESSAGE = "We are processing your request, an agent will contact you shortly.";

const CrmV2ChatRoomMessageModal = ({ open, lead, templates = [], onClose, onSend }) => {
  const [newNumber, setNewNumber] = useState(false);
  const [optIn, setOptIn] = useState(true);
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("Gupshup");
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [extra, setExtra] = useState("");
  const [localAttachments, setLocalAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewNumber(false);
    setOptIn(true);
    setTo(lead?.mobile || lead?.phone || "");
    setFrom(lead?.chatroom_from || "Gupshup");
    setTemplateId("");
    setBody(DEFAULT_MESSAGE);
    setExtra("");
    setLocalAttachments([]);
    setSending(false);
  }, [open, lead]);

  if (!open) return null;

  const applyTemplate = (value) => {
    setTemplateId(value);
    const template = templates.find((item) => String(item.id) === String(value));
    if (template) setBody(template.body_text || template.body_html || "");
  };

  const submit = async () => {
    if (!to.trim() || !body.trim() || sending) return;
    setSending(true);
    try {
      await onSend?.({ channel: "chatroom", template_id: templateId || undefined, to: to.trim(), from: from.trim(), body: body.trim(), extra: extra.trim(), opt_in: optIn, new_number: newNumber, attachment_files: localAttachments });
      onClose();
    } finally {
      setSending(false);
    }
  };

  const addLocalAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) setLocalAttachments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const removeLocalAttachment = (file) => {
    setLocalAttachments((current) => current.filter((item) => item !== file));
  };

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true" aria-label="Send ChatRoom Message">
      <div style={styles.dialog}>
        <div style={styles.header}>
          <strong>Send ChatRoom Message</strong>
          <button type="button" onClick={onClose} style={styles.close} aria-label="Close"><X size={20} /></button>
        </div>
        <div style={styles.body}>
          <div style={styles.recipientArea}>
            <div style={styles.leadName}>{lead?.contact_name || lead?.partner_name || lead?.company || lead?.name || "Lead"}</div>
            <div style={styles.routing}>
              <ToggleRow label="New number" checked={newNumber} onChange={setNewNumber} />
              <div style={styles.routeLine}>
                <label>To:</label>
                <input value={to} onChange={(event) => setTo(event.target.value)} placeholder="Phone number" style={styles.routeInput} />
                <span style={styles.routeText}>From:</span>
                <input value={from} onChange={(event) => setFrom(event.target.value)} style={styles.routeInput} />
              </div>
              <ToggleRow label="Opt-in" checked={optIn} onChange={setOptIn} />
            </div>
          </div>
          <div style={styles.messageLabel}>MESSAGE</div>
          <div style={styles.templateRow}>
            <span>Template</span>
            <select value={templateId} onChange={(event) => applyTemplate(event.target.value)} style={styles.templateSelect}>
              <option value="">ChatRoom: CRM Lead</option>
              {templates.map((template, i) => <option key={template.id ?? `tpl-${i}`} value={template.id}>{template.name}</option>)}
            </select>
          </div>
          <textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Write your message..." style={styles.textarea} />
          <div style={styles.extraLabel}>Extra</div>
          <input value={extra} onChange={(event) => setExtra(event.target.value)} style={styles.extraInput} />
          <div style={styles.attachRow}>
            <label htmlFor="crm-chatroom-attachment" style={styles.attach}><Paperclip size={14} /> ATTACH A FILE</label>
            <input id="crm-chatroom-attachment" type="file" multiple onChange={addLocalAttachments} style={styles.fileInput} />
            {localAttachments.map((file) => <span key={`${file.name}-${file.size}-${file.lastModified}`} style={styles.localAttachment}><Paperclip size={13} /><span>{file.name}</span><button type="button" onClick={() => removeLocalAttachment(file)} style={styles.removeAttachment} aria-label={`Remove ${file.name}`}><X size={13} /></button></span>)}
          </div>
          <div style={styles.filesHint}>Les fichiers sont uploadés puis liés au lead avant envoi (comme en email).</div>
        </div>
        <div style={styles.footer}>
          <button type="button" onClick={submit} disabled={sending || !to.trim() || !body.trim()} style={styles.primaryButton}><Send size={13} /> {sending ? "SENDING..." : "SEND"}</button>
          <button type="button" onClick={onClose} style={styles.linkButton}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange }) => (
  <label style={styles.toggleRow}><span>{label}</span><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /></label>
);

const styles = {
  backdrop: { position: "fixed", inset: 0, zIndex: 65, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.22)" },
  dialog: { width: "min(980px, 100vw)", minHeight: 510, display: "flex", flexDirection: "column", background: "#fff", boxShadow: "0 12px 35px rgba(0,0,0,.22)", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif', color: "#17202a" },
  header: { minHeight: 58, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderBottom: "1px solid #d9dee3", fontSize: 15 },
  close: { border: 0, background: "transparent", color: "#777", cursor: "pointer", display: "inline-flex", padding: 5 },
  body: { flex: 1, padding: "26px 16px 22px", overflowY: "auto" },
  recipientArea: { display: "grid", gridTemplateColumns: "minmax(190px, 1fr) minmax(420px, 1fr)", gap: 28, minHeight: 102 },
  leadName: { fontSize: 20, fontWeight: 600, whiteSpace: "pre-line" },
  routing: { display: "flex", flexDirection: "column", gap: 14, color: "#68717b", fontSize: 13 },
  toggleRow: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer" },
  routeLine: { display: "flex", alignItems: "center", gap: 5, borderBottom: "1px solid #7d858d", paddingBottom: 5 },
  routeInput: { minWidth: 0, flex: 1, border: 0, outline: 0, color: "#425d75", fontSize: 13, padding: "2px 4px" },
  routeText: { marginLeft: 12, whiteSpace: "nowrap" },
  messageLabel: { borderTop: "1px solid #dfe3e6", paddingTop: 14, marginTop: 12, color: "#425d75", fontSize: 11, fontWeight: 600 },
  templateRow: { display: "flex", alignItems: "center", gap: 24, marginTop: 16, color: "#425d75", fontSize: 13 },
  templateSelect: { width: 275, border: 0, borderBottom: "1px solid #d9dee3", background: "#fff", color: "#425d75", padding: "6px 2px", outline: 0 },
  textarea: { width: "100%", minHeight: 50, marginTop: 10, boxSizing: "border-box", border: 0, borderBottom: "1px solid #7d858d", outline: 0, resize: "vertical", padding: "10px 5px", background: "#f5f9fd", color: "#425d75", fontFamily: "inherit", fontSize: 13 },
  extraLabel: { marginTop: 14, color: "#6d7782", fontSize: 13 },
  extraInput: { width: "100%", boxSizing: "border-box", border: 0, outline: 0, padding: "8px 0", color: "#425d75" },
  attachRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, margin: "18px 0 4px 10px" },
  attach: { display: "inline-flex", alignItems: "center", gap: 4, color: "#6f668d", fontSize: 12, cursor: "pointer" },
  fileInput: { display: "none" },
  localAttachment: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px", border: "1px solid #b8d2df", background: "#eef7fb", color: "#315b6d", fontSize: 12 },
  removeAttachment: { display: "inline-flex", alignItems: "center", border: 0, background: "transparent", color: "#714b67", cursor: "pointer", padding: 0 },
  filesHint: { marginTop: 10, fontSize: 11.5, color: "#94a3b8" },
  attach: { display: "inline-flex", alignItems: "center", gap: 4, margin: "30px 0 12px 10px", color: "#6f668d", fontSize: 12, cursor: "pointer" },
  fileInput: { display: "none" },
  footer: { display: "flex", alignItems: "center", gap: 16, minHeight: 58, padding: "0 16px", borderTop: "1px solid #d9dee3" },
  primaryButton: { display: "inline-flex", alignItems: "center", gap: 6, border: 0, background: "#6f9bb4", color: "#fff", padding: "9px 12px", fontSize: 12, cursor: "pointer" },
  linkButton: { border: 0, background: "transparent", color: "#714b67", fontSize: 12, cursor: "pointer", padding: 0 },
};

export default CrmV2ChatRoomMessageModal;